import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo } from 'react';

import { PageIndex } from '@common/constant/PaginateConstant';
import { Optional } from '@common/type/CommonTypes';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, DialogActions, DialogTitle, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { TextArea } from '@components/atoms/textArea';
import BaseModalWrapper from '@components/share/modal/baseModalWrapper';
import { numberWithCommas } from '@helpers/utils';
import { useCreateCourseDiscussion } from '@hooks/course/courseDiscussion/useCreateCourseDiscussion';

const MAX_TITLE_LENGTH = 3000;

type HandleFetchCourseDiscussionListProps = {
  page: number;
  title?: string;
};

type CreateDiscussionDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  handleFetchCourseDiscussionList: ({ page, title }: HandleFetchCourseDiscussionListProps) => void;
};

const CreateDiscussionDialog: FC<CreateDiscussionDialogProps> = ({
  isOpen,
  setIsOpen,
  handleFetchCourseDiscussionList,
}) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();

  const router = useRouter();
  const idName = router.query.idName as Optional<string>;

  const {
    handleCreateCourseDiscussion,
    isCreateCourseDiscussionDataLoading,
    isCreateCourseDiscussionDataSuccess,
    handleResetCreateCourseDiscussion,
    isCreateCourseDiscussionDataError,
  } = useCreateCourseDiscussion({
    idName,
  });

  const createDiscussionSchemaValidate = yup.object({
    title: yup
      .string()
      .required()
      .test('validate length', 'error', (value) => (value || '').length <= MAX_TITLE_LENGTH),
  });

  const createDiscussionForm = useForm({
    mode: 'onChange',
    resolver: yupResolver(createDiscussionSchemaValidate),
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
  } = createDiscussionForm;

  const handleClose = useCallback(() => {
    setIsOpen(false);
    reset();
  }, [reset, setIsOpen]);

  const isDisabledSubmit = useMemo(() => {
    return isCreateCourseDiscussionDataLoading || !!errors?.title;
  }, [isCreateCourseDiscussionDataLoading, errors?.title]);

  const requiredError = errors.title?.type === 'required';
  const validateLengthError = errors.title?.type === 'validate length';

  const title = watch('title');

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (isCreateCourseDiscussionDataSuccess) {
      enqueueSnackbar({
        message: t('course_detail.course_discussion.create_discussion.success'),
        severity: 'success',
        isHideCloseIcon: true,
      });
      handleClose();
      handleResetCreateCourseDiscussion();
      handleFetchCourseDiscussionList({ page: PageIndex });
    }
  }, [
    enqueueSnackbar,
    handleClose,
    handleFetchCourseDiscussionList,
    handleResetCreateCourseDiscussion,
    isCreateCourseDiscussionDataSuccess,
    t,
  ]);

  useEffect(() => {
    if (isCreateCourseDiscussionDataError) {
      enqueueSnackbar({
        message: t('course_detail.course_discussion.create_discussion.failed'),
        severity: 'error',
        isHideCloseIcon: true,
      });
    }
  }, [enqueueSnackbar, isCreateCourseDiscussionDataError, t]);

  return (
    <BaseModalWrapper
      open={isOpen}
      onClose={handleClose}
      maxWidth={750}
      paperStyle={{
        width: 750,
        py: 0,
        maxHeight: { xs: 'calc(100vh - 48px)', md: 'calc(100vh - 96px)' },
        minHeight: { xs: '538px', md: '530px' },
      }}
      boxStyle={{ maxHeight: { xs: 'calc(100vh - 48px)', md: 'calc(100vh - 96px)' }, overflow: 'hidden' }}
      keepMounted={false}
      disabledBackDrop
    >
      <Box
        sx={{
          maxHeight: 'inherit',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: { md: '40px', xs: 3 }, pb: 1 }}>
          <Typography color={theme.palette.textV2.disabled} fontSize="48px" lineHeight="48px" fontWeight="300px" pb={3}>
            <FontAwesomeIcon icon="fa-light fa-comment" />
          </Typography>
          <Typography variant="h4">{t('course_detail.course_discussion.modal.title')}</Typography>
        </DialogTitle>

        <Controller
          name="title"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextArea
              value={value}
              onChange={onChange}
              minRows={10}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: requiredError ? theme.palette.errorV2.main : '',
                  },
                  '&:hover fieldset': {
                    borderColor: requiredError ? theme.palette.error.main : '',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: requiredError ? theme.palette.error.main : '',
                  },
                },
                overflowY: 'auto',
                mt: { xs: 1, md: 2 },
                pt: '1px',
                px: { xs: 1, md: 3 },
              }}
              placeholder={t('course_detail.course_discussion.modal.description_text_area')}
              inputProps={{ style: { padding: '9px 16px' } }}
              inputPropsStyle={{ style: { fontSize: 18, lineHeight: '22px', padding: 0 } }}
            />
          )}
        />

        <Box
          display="flex"
          justifyContent="space-between"
          gap="2px"
          sx={{ px: { xs: 1, md: 3 }, pb: { xs: 1, md: 3 } }}
        >
          <Box>
            {errors.title?.type === 'required' && (
              <Typography variant="helperText" color={requiredError ? theme.palette.errorV2.main : ''}>
                {t('course_detail.course_discussion.create_discussion.validate_length')}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography
              variant="helperText"
              color={validateLengthError ? theme.palette.errorV2.main : theme.palette.textV2.secondary}
            >
              {numberWithCommas((title || '').length)}
            </Typography>
            <Typography variant="helperText" color={theme.palette.textV2.secondary}>
              /
            </Typography>
            <Typography variant="helperText" color={theme.palette.textV2.secondary}>
              {numberWithCommas(MAX_TITLE_LENGTH)}
            </Typography>
          </Box>
        </Box>

        <DialogActions
          disableSpacing
          sx={{
            p: '8px 0 24px 0',
            px: { xs: 1, md: 3 },
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            gap: { xs: 2, md: 3 },
          }}
        >
          <Button variant="outlined" fullWidth onClick={handleClose}>
            {t('course_detail.course_discussion.modal.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disableElevation
            disabled={isDisabledSubmit}
            onClick={handleSubmit(handleCreateCourseDiscussion)}
          >
            {t('course_detail.course_discussion.modal.submit')}
          </Button>
        </DialogActions>
      </Box>
    </BaseModalWrapper>
  );
};

export default CreateDiscussionDialog;
